import React, { useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { UploadCloud, FileText, X, Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import { bulkImportAssets } from '../services/api';
import { toast } from 'sonner';

const CSV_TEMPLATE =
  'name,type,status,criticality,customer,site,location,serialNumber,notes\n' +
  '"Pump 01","Mechanical","operational","high","Acme Widgets","Manchester Plant","Boiler room","SN-1234","Weekly greasing"\n' +
  '"Motor M2","Electrical","maintenance","medium","Acme Widgets","","",""\n';

/**
 * Bulk-import assets from a CSV file. Two-step flow:
 *   1) Dry-run so the admin sees exactly which rows will be imported vs
 *      rejected and why, without touching the database.
 *   2) Actual import only when the admin explicitly confirms.
 * Onboarding a new customer with hundreds of assets used to mean typing
 * each one — this shrinks it to a paste-from-Excel job.
 */
export function BulkImportAssetsDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [dryReport, setDryReport] = useState(null);
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setDryReport(null);
    setImporting(false);
    setValidating(false);
    setDragOver(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lutrom-assets-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error('Please choose a .csv file');
      return;
    }
    setFile(f);
    setDryReport(null);
  };

  const runValidation = async () => {
    if (!file) return;
    setValidating(true);
    try {
      const rep = await bulkImportAssets(file, true);
      setDryReport(rep);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read that CSV');
    } finally {
      setValidating(false);
    }
  };

  const runImport = async () => {
    if (!file || !dryReport) return;
    setImporting(true);
    try {
      const rep = await bulkImportAssets(file, false);
      toast.success(`Imported ${rep.imported.length} asset${rep.imported.length === 1 ? '' : 's'}`);
      onImported?.(rep);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    pickFile(f);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl" data-testid="bulk-import-assets-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import assets</DialogTitle>
          <DialogDescription>
            Drop a CSV to add many assets at once. Rows are matched to existing customers
            and sites by name — we never auto-create either.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <button
            type="button"
            onClick={downloadTemplate}
            className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
            data-testid="download-asset-template"
          >
            <Download className="h-3.5 w-3.5" /> Download CSV template
          </button>

          {!file ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragOver ? 'border-slate-900 bg-slate-50' : 'border-slate-300 hover:border-slate-500'
              }`}
              data-testid="asset-csv-dropzone"
            >
              <UploadCloud className="h-10 w-10 mx-auto text-slate-400 mb-2" />
              <div className="text-sm font-medium text-slate-900">Drop a CSV here, or click to pick a file</div>
              <div className="text-xs text-slate-500 mt-1">
                Required column: <code className="font-mono">name</code>. Optional: type, status, criticality, customer, site, location, serialNumber, notes.
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
                data-testid="asset-csv-input"
              />
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg p-3 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-5 w-5 text-slate-500 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" data-testid="asset-csv-filename">{file.name}</div>
                  <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
              </div>
              <button
                type="button"
                onClick={reset}
                className="text-slate-400 hover:text-slate-700"
                data-testid="asset-csv-clear"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {file && !dryReport && (
            <Button
              onClick={runValidation}
              disabled={validating}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="asset-csv-validate"
            >
              {validating ? 'Checking…' : 'Preview import'}
            </Button>
          )}

          {dryReport && (
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto" data-testid="asset-csv-report">
              <div className="p-3 flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  <strong className="text-slate-900">{dryReport.totalRows}</strong> row{dryReport.totalRows === 1 ? '' : 's'} found
                </span>
                <span className="inline-flex items-center gap-3">
                  <span className="text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" /> {dryReport.prepared} will import
                  </span>
                  {dryReport.errors.length > 0 && (
                    <span className="text-amber-700 inline-flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" /> {dryReport.errors.length} skipped
                    </span>
                  )}
                </span>
              </div>
              {dryReport.errors.map((err) => (
                <div key={err.row} className="p-2.5 text-xs bg-amber-50 text-amber-900">
                  <span className="font-mono">Row {err.row}</span> — {err.error}
                </div>
              ))}
              {dryReport.prepared === 0 && dryReport.errors.length === 0 && (
                <div className="p-4 text-xs text-slate-500 text-center">Nothing to import.</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={importing}>Cancel</Button>
          <Button
            onClick={runImport}
            disabled={!dryReport || dryReport.prepared === 0 || importing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="asset-csv-import"
          >
            {importing
              ? 'Importing…'
              : dryReport
                ? `Import ${dryReport.prepared} asset${dryReport.prepared === 1 ? '' : 's'}`
                : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkImportAssetsDialog;
